@if (isLoading) {
  <div class="spinner-overlay">
    <mat-spinner></mat-spinner>
  </div>
}

<h1>Subsample - Cube Trend Absolute Permeability Results</h1>

@if (!isLoading) {
  <mat-card class="results-card">
    @if (hasAnyResults()) {
      <div class="results-content">
        <div class="result-columns">
          <div class="result-column w-50">
            <app-simulation-overview
              [simulationResponse]="simulationResponse"
              [directions]="['x', 'y', 'z']"
            ></app-simulation-overview>
            <mat-expansion-panel [expanded]="false" style="margin-top: 10px;">
              <mat-expansion-panel-header>
                <mat-panel-title>Simulation Processing Times</mat-panel-title>
              </mat-expansion-panel-header>
              <app-simulation-directions-table
                [simulationResponse]="simulationResponse"
                [directions]="['x', 'y', 'z']"
              ></app-simulation-directions-table>
            </mat-expansion-panel>
          </div>
          <div class="result-column w-50">
            <mat-expansion-panel [expanded]="true">
              <mat-expansion-panel-header>
                <mat-panel-title>Simulation Parameters</mat-panel-title>
              </mat-expansion-panel-header>
              <div>
                <form [formGroup]="directionsForm">
                  <div class="input-row">
                    <span>Directions</span>
                    <div class="direction-options">
                      @for (direction of directions; track direction) {
                        <mat-checkbox [formControlName]="direction">
                          {{ direction.toUpperCase() }}
                        </mat-checkbox>
                      }
                    </div>
                  </div>
                </form>
                <form [formGroup]="cubeSizesForm">
                  <div class="input-row">
                    <span>Cube size</span>
                    <div class="cube-size-options">
                      @for (size of cubeSizes; track size) {
                        <div class="cube-size-item">
                          <mat-checkbox [formControlName]="size.cubeSizeVoxels.toString()"></mat-checkbox>
                          <span class="cube-size-label">{{ size.cubeSizeFraction }}% ({{ size.cubeSizeVoxels }})</span>
                        </div>
                      }
                    </div>
                  </div>
                </form>
              </div>
            </mat-expansion-panel>
          </div>
        </div>
        @if (!allResultsInvalid) {
          <div class="panels-side-by-side">
            <mat-expansion-panel [expanded]="true" class="chart-panel">
              <mat-expansion-panel-header>
                <mat-panel-title>Absolute Permeability Trend</mat-panel-title>
              </mat-expansion-panel-header>
              @if (chartOptions) {
                <div class="chart-container">
                  <highcharts-chart
                    [Highcharts]="Highcharts"
                    [options]="chartOptions"
                    [oneToOne]="true"
                    (chartInstance)="saveChartInstance($event)"
              style="
                width: 100%;
                height: 450px;
                display: block;
                border: 1px solid var(--main_borderColor);
              "
                  ></highcharts-chart>
                </div>
              } @else {
                <div class="loading-chart">
                  <p>Loading chart...</p>
                </div>
              }
            </mat-expansion-panel>
            @if (displayedCoefficients.length > 0) {
              <mat-expansion-panel [expanded]="true" class="coefficients-panel">
                <mat-expansion-panel-header>
                  <mat-panel-title>Trend Coefficients</mat-panel-title>
                </mat-expansion-panel-header>
                <table class="coefficients-table">
                  <thead>
                    <tr>
                      <th>Direction - Cube Size</th>
                      <th>Angular Coeff.</th>
                      <th>Exp. Coeff.</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (coef of displayedCoefficients; track coef) {
                      <tr>
                        <td class="direction-cell" [style.background-color]="coef.color" [style.color]="'white'">
                          {{ coef.direction }} Size {{ coef.cubeSize }}
                        </td>
                        <td class="coef-value">{{ toScientificNotation(coef.angularCoefficient) }}</td>
                        <td class="coef-value">{{ coef.exponentialCoefficient.toFixed(2) }}</td>
                      </tr>
                    }
                  </tbody>
                </table>
                <div class="coefficients-note">
                  <p><em>Note: Least squares coefficients for power law model</em></p>
                </div>
              </mat-expansion-panel>
            }
          </div>
          <mat-expansion-panel [expanded]="true">
            <mat-expansion-panel-header>
              <mat-panel-title>Chart data</mat-panel-title>
              <button
                mat-raised-button
                color="primary"
                type="button"
                [disabled]="!isDataSelected"
                (click)="downloadResults(); $event.stopPropagation()"
                class="header-button"
                >
                <mat-icon>download</mat-icon>
                Download
              </button>
            </mat-expansion-panel-header>
            @if (tableDataPoints.length) {
              <div class="scroll-x">
                <table class="data-table">
                  <thead>
                    <tr>
                      @for (direction of directions; track direction) {
                        @if (selectedDirections['direction' + direction]) {
                          @for (size of cubeSizes; track size; let sizeIndex = $index) {
                            @if (selectedCubeSizes['size' + size.cubeSizeVoxels]) {
                              <th
                                colspan="2"
                                class="size-header"
                                [style.background-color]="getColorForDirection(direction, sizeIndex)"
                                [style.color]="'#fff'"
                                >
                                {{ direction.toUpperCase() }} Size {{ size.cubeSizeVoxels }}
                              </th>
                            }
                          }
                        }
                      }
                    </tr>
                    <tr>
                      @for (direction of directions; track direction) {
                        @if (selectedDirections['direction' + direction]) {
                          @for (size of cubeSizes; track size; let sizeIndex = $index) {
                            @if (selectedCubeSizes['size' + size.cubeSizeVoxels]) {
                              <th
                                class="direction-header"
                                [style.background-color]="getColorForDirection(direction, sizeIndex)"
                                [style.color]="'#fff'"
                              >Porosity (%)</th>
                              <th
                                class="direction-header"
                                [style.background-color]="getColorForDirection(direction, sizeIndex)"
                                [style.color]="'#fff'"
                              >Permeability (mD)</th>
                            }
                          }
                        }
                      }
                    </tr>
                  </thead>
                  <tbody>
                    @for (row of tableDataPoints; track row) {
                      <tr>
                        @for (direction of directions; track direction) {
                          @if (selectedDirections['direction' + direction]) {
                            @for (size of cubeSizes; track size; let sizeIndex = $index) {
                              @if (selectedCubeSizes['size' + size.cubeSizeVoxels]) {
                                <td
                                  [style.background-color]="'#fff'"
                                  [style.color]="'#000'"
                                  >
                                  {{ row[direction.toUpperCase() + '_' + size.cubeSizeVoxels + '_phi'] || '—' }}
                                </td>
                                <td
                                  [style.background-color]="'#fff'"
                                  [style.color]="'#000'"
                                  >
                                  {{ row[direction.toUpperCase() + '_' + size.cubeSizeVoxels + '_kabs'] || '—' }}
                                </td>
                              }
                            }
                          }
                        }
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            } @else {
              <div class="no-table-data">
                <p>No data points selected. Please select directions and cube sizes to display results.</p>
              </div>
            }
          </mat-expansion-panel>
        } @else {
          <div class="no-results">
            <mat-icon class="no-results-icon">info</mat-icon>
            <p>No result available.</p>
          </div>
        }
      </div>
    } @else {
      <div class="no-results padding">
        <mat-icon class="no-results-icon">info</mat-icon>
        <p>No result available for this simulation yet.</p>
      </div>
    }
  </mat-card>
}




